/**
 * Format Exporter — exports flight plans to ICAO FPL, Garmin FPL, GPX, KML, GeoJSON.
 * Platform-agnostic: pure string transformations.
 */
import type { FlightPlan, ExportFormat } from './types';
import { FlightPlanError } from './errors';

export class FormatExporter {
  export(plan: FlightPlan, format: ExportFormat): string {
    switch (format) {
      case 'icao-fpl': return this.exportIcaoFpl(plan);
      case 'garmin-fpl': return this.exportGarminFpl(plan);
      case 'gpx': return this.exportGpx(plan);
      case 'kml': return this.exportKml(plan);
      case 'geojson': return this.exportGeoJson(plan);
      default: throw new FlightPlanError(`Unknown export format: ${format}`, 'EXPORT_ERROR');
    }
  }

  private exportIcaoFpl(plan: FlightPlan): string {
    const wps = plan.waypoints;
    const route = wps.slice(1, -1).map(w => w.identifier).join(' ') || 'DCT';
    const dep = plan.departure.identifier; const dest = plan.destination.identifier;
    const eet = this.fmtEet(plan.totalTimeMinutes);
    const lines = [
      `(FPL-${plan.name || 'PLAN'}-${plan.flightRules === 'IFR' ? 'I' : 'V'}G`,
      `-1/ZZZZ/L`,
      `-S`,
      `-${dep}0000`,
      `-N0${plan.trueAirspeed}A${String(Math.round(plan.cruiseAltitude / 100)).padStart(3, '0')} ${route}`,
      `-${dest}${eet}`,
      `-0)`,
    ];
    return lines.join('\n');
  }

  private exportGarminFpl(plan: FlightPlan): string {
    const wps = plan.waypoints.map(wp => `    <waypoint>
      <identifier>${this.xmlEsc(wp.identifier)}</identifier>
      <type>${wp.type === 'airport' ? 'AIRPORT' : wp.type === 'navaid' ? 'VOR' : 'INT'}</type>
      <lat>${wp.latitude.toFixed(6)}</lat>
      <lon>${wp.longitude.toFixed(6)}</lon>
    </waypoint>`).join('\n');
    const rte = plan.waypoints.map(wp =>
      `      <route-point><waypoint-identifier>${this.xmlEsc(wp.identifier)}</waypoint-identifier></route-point>`
    ).join('\n');
    return `<?xml version="1.0" encoding="utf-8"?>
<flight-plan xmlns="http://www8.garmin.com/xmlschemas/FlightPlan/v1">
  <waypoint-table>
${wps}
  </waypoint-table>
  <route>
    <route-name>${this.xmlEsc(plan.name || 'Route')}</route-name>
${rte}
  </route>
</flight-plan>`;
  }

  private exportGpx(plan: FlightPlan): string {
    const wps = plan.waypoints.map(wp =>
      `  <wpt lat="${wp.latitude.toFixed(6)}" lon="${wp.longitude.toFixed(6)}">
    <name>${this.xmlEsc(wp.identifier)}</name>
    <ele>${wp.elevation ?? 0}</ele>
  </wpt>`).join('\n');
    const rte = plan.waypoints.map(wp =>
      `    <rtept lat="${wp.latitude.toFixed(6)}" lon="${wp.longitude.toFixed(6)}">
      <name>${this.xmlEsc(wp.identifier)}</name>
    </rtept>`).join('\n');
    return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" xmlns="http://www.topografix.com/GPX/1/1">
${wps}
  <rte>
    <name>${this.xmlEsc(plan.name || 'Route')}</name>
${rte}
  </rte>
</gpx>`;
  }

  private exportKml(plan: FlightPlan): string {
    const coords = plan.waypoints.map(wp =>
      `${wp.longitude.toFixed(6)},${wp.latitude.toFixed(6)},${wp.elevation ?? 0}`
    ).join('\n            ');
    const placemarks = plan.waypoints.map(wp =>
      `    <Placemark><name>${this.xmlEsc(wp.identifier)}</name>
      <Point><coordinates>${wp.longitude.toFixed(6)},${wp.latitude.toFixed(6)},0</coordinates></Point>
    </Placemark>`).join('\n');
    return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${this.xmlEsc(plan.name || 'Flight Plan')}</name>
${placemarks}
    <Placemark>
      <name>Route</name>
      <LineString><coordinates>${coords}</coordinates></LineString>
    </Placemark>
  </Document>
</kml>`;
  }

  private exportGeoJson(plan: FlightPlan): string {
    const features: object[] = [];
    for (const wp of plan.waypoints) {
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [wp.longitude, wp.latitude] },
        properties: { identifier: wp.identifier, name: wp.name, type: wp.type, altitude: wp.plannedAltitude },
      });
    }
    features.push({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: plan.waypoints.map(wp => [wp.longitude, wp.latitude]),
      },
      properties: { name: plan.name, flightRules: plan.flightRules },
    });
    return JSON.stringify({ type: 'FeatureCollection', features }, null, 2);
  }

  private fmtEet(minutes: number): string {
    const h = Math.floor(minutes / 60); const m = Math.round(minutes % 60);
    return `${String(h).padStart(2, '0')}${String(m).padStart(2, '0')}`;
  }

  private xmlEsc(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}
